import { apiJson } from '@/lib/http';

interface ApiResponse<T> {
  success?: boolean;
  message?: string;
  data: T;
}

export interface ChatMessageObject {
  message: string;
  instructions: string;
}

export interface ChatSessionSummaryDto {
  id: string;
  teamId?: string;
  teamName?: string;
  userId: string;
  createdAt: string;
  textIn?: number;
  textOut?: number;
  audioIn?: number;
  audioOut?: number;
  promptTokens?: number;
  completionTokens?: number;
  caller?: string;
  summary?: string;
  sentiment?: string;
  duration?: number;
  reason?: string;
  action?: string;
  bookingDone?: boolean;
  successfull?: boolean;
  sttProvider?: string;
  sttLanguage?: string;
}

export interface ChatSessionDetailDto extends ChatSessionSummaryDto {
  messages: Array<{ role: string; content: string; timestamp?: string; order?: number; source?: string, isVoice?: boolean }>;
}

export interface ChatStyleConfig {
  styles: {
    theme: string;
    primary_color: string;
    secondary_color: string;
    accent_color: string;
    button_color: string;
    brand_name: string;
    auto_open_chat_window_after: number;
    align_chat_button: string;
    message_placeholder: string;
    tagline: string;
    welcome_message: string;
    collect_user_feedback: boolean;
    regenerate_messages: boolean;
    continue_showing_suggested_messages: boolean;
    hidden_paths: string[];
    auto_open_bubble_messages: boolean;
    auto_open_bubble_messages_after: number;
  };
  initialMessages: ChatMessageObject[];
  bubbleMessages: ChatMessageObject[];
}

const apiCall = async <T>(endpoint: string, options: RequestInit = {}) => apiJson<ApiResponse<T> | T>(endpoint, options);

export interface ChatSettings {
  id: string;
  teamId: string;
  projectId?: string | null;
  createdAt: string;
  styles: ChatStyleConfig['styles'];
  initialMessages: ChatMessageObject[];
  bubbleMessages: ChatMessageObject[];
  welcomeMessage?: string;
  welcomeLogo?: string;
  hmacKey?: string;
  customInstructions?: string;
  customGreeting?: string;
  logo?: string;
  language?: string;
  voiceId?: string;
  backgroundSound?: string;
  sttProvider?: string;
  ttsProvider?: string;
  phoneNumber?: string;
  defaultTransferNumber?: string;
  restaurantCode?: string;
  askLastname?: boolean;
  maxGroupSize?: number;
  maxGroupSizeHandling?: "transfer" | "sms";
  transferTakeout?: boolean;
}

const unwrap = <T>(res: ApiResponse<T> | T): T => (res as ApiResponse<T>)?.data ?? (res as T);

type MaybeChatSettingsRaw = {
  id?: string;
  Id?: string;
  team_id?: string;
  teamId?: string;
  TeamId?: string;
  project_id?: string | null;
  projectId?: string | null;
  ProjectId?: string | null;
  created_at?: string | Date;
  createdAt?: string | Date;
  CreatedAt?: string | Date;
  styles?: ChatStyleConfig['styles'];
  Styles?: ChatStyleConfig['styles'];
  initial_messages?: unknown;
  initialMessages?: unknown;
  InitialMessages?: unknown;
  bubble_messages?: unknown;
  bubbleMessages?: unknown;
  BubbleMessages?: unknown;
  welcome_message?: string;
  welcomeMessage?: string;
  WelcomeMessage?: string;
  welcome_logo?: string;
  welcomeLogo?: string;
  WelcomeLogo?: string;
  hmac_key?: string;
  hmacKey?: string;
  HmacKey?: string;
  custom_instructions?: string;
  customInstructions?: string;
  CustomInstructions?: string;
  custom_greeting?: string;
  customGreeting?: string;
  CustomGreeting?: string;
  logo?: string;
  Logo?: string;
  language?: string;
  Language?: string;
  voice_id?: string;
  voiceId?: string;
  VoiceId?: string;
  background_sound?: string;
  backgroundSound?: string;
  BackgroundSound?: string;
  stt_provider?: string;
  sttProvider?: string;
  SttProvider?: string;
  tts_provider?: string;
  ttsProvider?: string;
  TtsProvider?: string;
  phone_number?: string;
  phoneNumber?: string;
  PhoneNumber?: string;
  default_transfer_number?: string;
  defaultTransferNumber?: string;
  DefaultTransferNumber?: string;
  restaurant_code?: string;
  restaurantCode?: string;
  RestaurantCode?: string;
  ask_lastname?: boolean;
  askLastname?: boolean;
  AskLastname?: boolean;
  max_group_size?: number;
  maxGroupSize?: number;
  MaxGroupSize?: number;
  max_group_size_handling?: string;
  maxGroupSizeHandling?: string;
  MaxGroupSizeHandling?: string;
  transfer_takeout?: boolean;
  transferTakeout?: boolean;
  TransferTakeout?: boolean;
} | null;

const ensureArrayOfObjects = (value: unknown): ChatMessageObject[] => {
  if (!Array.isArray(value)) return [];
  return (value as Array<Record<string, unknown> | string>).map((item) => {
    if (typeof item === 'string') {
      return { message: item, instructions: '' } as ChatMessageObject;
    }
    const obj = item as Record<string, unknown>;
    const message = typeof obj.message === 'string'
      ? obj.message
      : typeof obj['Message'] === 'string'
        ? (obj['Message'] as string)
        : '';
    const instructions = typeof obj.instructions === 'string'
      ? obj.instructions
      : typeof obj['Instructions'] === 'string'
        ? (obj['Instructions'] as string)
        : '';
    return { message, instructions } as ChatMessageObject;
  });
};

const normalizeSettings = (raw: MaybeChatSettingsRaw): ChatSettings | null => {
  if (!raw || raw.id==undefined) return null;
  const id = raw.id ?? raw.Id;
  const teamId = raw.team_id ?? raw.teamId ?? raw.TeamId;
  const projectId = raw.project_id ?? raw.projectId ?? raw.ProjectId ?? null;
  const createdAt = raw.created_at ?? raw.createdAt ?? raw.CreatedAt ?? new Date().toISOString();
  const styles = (raw.styles ?? raw.Styles) as ChatStyleConfig['styles'] | undefined;
  const initial = (raw.initial_messages ?? raw.initialMessages ?? raw.InitialMessages) as unknown;
  const bubble = (raw.bubble_messages ?? raw.bubbleMessages ?? raw.BubbleMessages) as unknown;
  const welcomeMessage = (raw.welcome_message ?? raw.welcomeMessage ?? raw.WelcomeMessage) as string | undefined;
  const welcomeLogo = (raw.welcome_logo ?? raw.welcomeLogo ?? raw.WelcomeLogo) as string | undefined;
  const hmacKey = (raw.hmac_key ?? raw.hmacKey ?? raw.HmacKey) as string | undefined;
  const customInstructions = (raw.custom_instructions ?? raw.customInstructions ?? raw.CustomInstructions) as string | undefined;
  const customGreeting = (raw.custom_greeting ?? raw.customGreeting ?? raw.CustomGreeting) as string | undefined;
  const logo = (raw.logo ?? (raw as unknown as Record<string, unknown>)['Logo']) as string | undefined;
  
  const language = (raw.language ?? raw.Language) as string | undefined;
  const voiceId = (raw.voice_id ?? raw.voiceId ?? raw.VoiceId) as string | undefined;
  const backgroundSound = (raw.background_sound ?? raw.backgroundSound ?? raw.BackgroundSound) as string | undefined;
  const sttProvider = (raw.stt_provider ?? raw.sttProvider ?? raw.SttProvider) as string | undefined;
  const ttsProvider = (raw.tts_provider ?? raw.ttsProvider ?? raw.TtsProvider) as string | undefined;
  const phoneNumber = (raw.phone_number ?? raw.phoneNumber ?? raw.PhoneNumber) as string | undefined;
  const defaultTransferNumber = (raw.default_transfer_number ?? raw.defaultTransferNumber ?? raw.DefaultTransferNumber) as string | undefined;
  const restaurantCode = (raw.restaurant_code ?? raw.restaurantCode ?? raw.RestaurantCode) as string | undefined;
  const askLastname = (raw.ask_lastname ?? raw.askLastname ?? raw.AskLastname) as boolean | undefined;
  const maxGroupSize = (raw.max_group_size ?? raw.maxGroupSize ?? raw.MaxGroupSize) as number | undefined;
  const rawMaxGroupSizeHandling = (raw.max_group_size_handling ?? raw.maxGroupSizeHandling ?? raw.MaxGroupSizeHandling) as string | undefined;
  const maxGroupSizeHandling = rawMaxGroupSizeHandling === 'sms' ? 'sms' : 'transfer';
  const transferTakeout = (raw.transfer_takeout ?? raw.transferTakeout ?? raw.TransferTakeout) as boolean | undefined;

  return {
    id: String(id),
    teamId: String(teamId),
    projectId,
    createdAt: typeof createdAt === 'string' ? createdAt : new Date(createdAt).toISOString(),
    styles: (styles ?? {
      theme: 'dark',
      primary_color: '#8b5cf6',
      secondary_color: '#f8fafc',
      accent_color: '#f1f5f9',
      button_color: '#000000',
      brand_name: 'Recallio.AI',
      auto_open_chat_window_after: 3,
      align_chat_button: 'right',
      message_placeholder: 'Type your question...',
      tagline: 'You can ask me anything',
      welcome_message: 'Ask anything about transactions, listings...',
      collect_user_feedback: true,
      regenerate_messages: true,
      continue_showing_suggested_messages: false,
      hidden_paths: [],
      auto_open_bubble_messages: true,
      auto_open_bubble_messages_after: 3,
    }),
    initialMessages: ensureArrayOfObjects(initial),
    bubbleMessages: ensureArrayOfObjects(bubble),
    welcomeMessage,
    welcomeLogo,
    hmacKey,
    customInstructions,
    customGreeting,
    logo,
    language,
    voiceId,
    backgroundSound,
    sttProvider,
    ttsProvider,
    phoneNumber,
    defaultTransferNumber,
    restaurantCode,
    askLastname: askLastname ?? true,
    maxGroupSize: maxGroupSize ?? -1,
    maxGroupSizeHandling,
    transferTakeout: transferTakeout ?? false,
  };
};

export const chatbotService = {
  async listSessions(params: { userId?: string; projectId?: string; limit?: number; offset?: number; startDate?: string; endDate?: string; includeAll?: boolean } = {}) {
    const search = new URLSearchParams();
    if (params.userId) search.set('userId', params.userId);
    if (params.projectId) search.set('projectId', params.projectId);
    if (params.limit) search.set('limit', String(params.limit));
    if (typeof params.offset === 'number') search.set('offset', String(params.offset));
    if (params.startDate) search.set('startDate', params.startDate);
    if (params.endDate) search.set('endDate', params.endDate);
    if (params.includeAll) search.set('includeAll', 'true');
    return apiCall<ChatSessionSummaryDto[]>(`/Chat/sessions${search.toString() ? `?${search.toString()}` : ''}`)
      .then(unwrap);
  },

  async listAllSessions(limit: number = 200) {
    return this.listSessions({ limit, includeAll: true });
  },

  async getSession(sessionId: string) {
    return apiCall<ChatSessionDetailDto>(`/Chat/sessions/${encodeURIComponent(sessionId)}`)
      .then(unwrap);
  },
  async getSettings(projectId?: string): Promise<ChatSettings | null> {
    const endpoint = `/Chat/settings${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`;
    const res = await apiCall<MaybeChatSettingsRaw>(endpoint);
    const data = unwrap<MaybeChatSettingsRaw>(res);
    return normalizeSettings(data);
  },

  async listSettings(projectId?: string): Promise<ChatSettings[]> {
    const endpoint = `/Chat/settingsList${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`;
    const res = await apiCall<MaybeChatSettingsRaw[] | { data: MaybeChatSettingsRaw[] }>(endpoint);
    const maybeArray = (res as ApiResponse<MaybeChatSettingsRaw[]>)?.data ?? (res as MaybeChatSettingsRaw[]);
    const list = Array.isArray(maybeArray) ? maybeArray : [];
    return list.map(normalizeSettings).filter((x): x is ChatSettings => Boolean(x));
  },

  async getSettingsById(id: string): Promise<ChatSettings | null> {
    const res = await apiCall<MaybeChatSettingsRaw>(`/Chat/settings/${encodeURIComponent(id)}`);
    const data = unwrap<MaybeChatSettingsRaw>(res);
    return normalizeSettings(data);
  },

  async createSettings(projectId?: string): Promise<ChatSettings> {
    const endpoint = `/Chat/create${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ''}`;
    const res = await apiCall<MaybeChatSettingsRaw>(endpoint, { method: 'POST' });
    const data = unwrap<MaybeChatSettingsRaw>(res);
    const settings = normalizeSettings(data);
    if (!settings) throw new Error('Failed to create chatbot settings');
    return settings;
  },

  async updateSettings(settings: ChatSettings): Promise<ChatSettings> {
    const body: {
      id: string;
      teamId: string;
      projectId?: string | null;
      createdAt: string;
      styles: ChatStyleConfig['styles'];
      initialMessages: ChatMessageObject[];
      bubbleMessages: ChatMessageObject[];
      welcomeMessage: string;
      welcomeLogo: string;
      hmacKey: string;
      customInstructions: string;
      customGreeting: string;
      logo?: string;
      language?: string;
      voiceId?: string;
      backgroundSound?: string;
      sttProvider?: string;
      ttsProvider?: string;
      phoneNumber?: string;
      defaultTransferNumber?: string;
      restaurantCode?: string;
      askLastname?: boolean;
      maxGroupSize?: number;
      maxGroupSizeHandling?: "transfer" | "sms";
      transferTakeout?: boolean;
    } = {
      id: settings.id,
      teamId: settings.teamId,
      projectId: settings.projectId ?? null,
      createdAt: settings.createdAt,
      styles: settings.styles,
      initialMessages: settings.initialMessages,
      bubbleMessages: settings.bubbleMessages,
      welcomeMessage: settings.welcomeMessage ?? '',
      welcomeLogo: settings.welcomeLogo ?? '',
      hmacKey: settings.hmacKey ?? '',
      customInstructions: settings.customInstructions ?? '',
      customGreeting: settings.customGreeting ?? '',
      logo: settings.logo,
      language: settings.language,
      voiceId: settings.voiceId,
      backgroundSound: settings.backgroundSound,
      sttProvider: settings.sttProvider,
      ttsProvider: settings.ttsProvider,
      phoneNumber: settings.phoneNumber,
      defaultTransferNumber: settings.defaultTransferNumber,
      restaurantCode: settings.restaurantCode,
      askLastname: settings.askLastname,
      maxGroupSize: settings.maxGroupSize,
      maxGroupSizeHandling: settings.maxGroupSizeHandling ?? "transfer",
      transferTakeout: settings.transferTakeout,
    };
    const res = await apiCall<MaybeChatSettingsRaw>(`/Chat/update`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    const data = unwrap<MaybeChatSettingsRaw>(res);
    const normalized = normalizeSettings(data);
    if (!normalized) throw new Error('Failed to update chatbot settings');
    return normalized;
  },

  async getChatId(): Promise<string | null> {
    const res = await apiCall<{ chatbotId: string | null }>(`/Chat/id`);
    const data = (res as ApiResponse<{ chatbotId: string | null }>)?.data ?? (res as { chatbotId: string | null });
    return data?.chatbotId ?? null;
  },

  async createChatId(): Promise<string> {
    const res = await apiCall<{ chatbotId: string }>(`/Chat/id`, { method: 'POST' });
    const data = (res as ApiResponse<{ chatbotId: string }>)?.data ?? (res as { chatbotId: string });
    return data.chatbotId;
  },
  async getStyle(chatbotId: string): Promise<ChatStyleConfig> {
    const res = await apiCall<ChatStyleConfig>(`/Chat/style/${encodeURIComponent(chatbotId)}`);
    if ((res as ApiResponse<ChatStyleConfig>)?.data) {
      return (res as ApiResponse<ChatStyleConfig>).data;
    }
    return res as ChatStyleConfig;
  },
  async deleteSettings(id: string): Promise<void> {
    await apiCall<unknown>(`/Chat/settings/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
};


